import { useEffect, useState } from "react";
import { Button, Offcanvas, Tab, Tabs, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { useDispatch, useSelector } from "react-redux";

import { notificationRead, notificationsReadAll } from "../api";
import { NotifCard } from "./notifcard";
import { getNotificationsBetween, getNotificationsPage } from "../actions/notifications/notificationsgetpage";
import { actionTypeSelector, notificationsSelector, unreadNotifCountSelector } from "../reducers";
import { localized } from "../common/utils";

let page = -1;
let loading = false;
let dateTimer;

const units = ["years", "months", "weeks", "days", "hours", "minutes", "seconds"];

export function NotifCanvas({ show, setShow }) {
  const { t } = useTranslation();
  const { resolvedLanguage } = localized;
  const rtf = new Intl.RelativeTimeFormat(resolvedLanguage);
  const dispatch = useDispatch();

  const unreadNotifCount = useSelector(unreadNotifCountSelector);
  const notifications = useSelector(notificationsSelector);
  useEffect(() => {
    handleNotificationFilter(notificationFilter);
  }, []);

  const handleClose = () => {
    setShow(false);
    markAllAsRead();
  };

  const [date, setDate] = useState(Date.now());
  const updateDate = () => setDate(Date.now());
  useEffect(() => {
    // update time ago every 30 seconds when notifications are shown
    if (show === true) {
      updateDate();
      dateTimer = setInterval(updateDate, 30000);
    } else {
      clearInterval(dateTimer);
    }
  }, [show]);

  useEffect(() => {
    // update time when notifications are changed
    if (show === true) {
      updateDate();
    }
  }, [notifications]);

  const duration = (createdDate) => {
    const diff = DateTime.fromJSDate(createdDate).diff(DateTime.fromMillis(date), units);
    if (diff.years) {
      return rtf.format(diff.years, "years");
    } else if (diff.months) {
      return rtf.format(diff.months, "months");
    } else if (diff.weeks) {
      return rtf.format(diff.weeks, "weeks");
    }

    for (const unit of units) {
      const value = Math.round(diff.values[unit]);
      if (unit === units[6]) {
        if (value > -30) return t("JustNow", "Juuri nyt");
        else return t("LessThanMinuteAgo", "Alle minuutti sitten");
      } else if (value < 0) {
        return rtf.format(value, unit);
      }
    }
  };

  const read = ({ target }) => {
    notificationRead(target.parentElement.parentElement.parentElement.dataset.id, target.checked);
    dispatch({
      type: "SET_UNREAD_NOTIFICATIONS_COUNT",
      payload: unreadNotifCount + (target.checked ? -1 : 1),
    });
  };

  // divide notifications array to three arrays by notification.type
  const [notifs, msgs, tasks] = notifications.reduce(
    ([a, b, c], cur) => {
      if (cur.type === "NOTIFICATION") a.push(cur);
      else if (cur.type === "MESSAGE") b.push(cur);
      else c.push(cur);
      return [a, b, c];
    },
    [[], [], []],
  );

  const actionType = useSelector(actionTypeSelector);
  if (actionType === "GET_NOTIFICATIONS_LOADING_DONE") {
    loading = false;
  }

  const [notificationFilter, setNotificationFilter] = useState("today");

  const notifScroll = ({ target }) => {
    const top = target.scrollTop;
    const bottom = target.scrollHeight - target.offsetHeight - top;
    if (!loading && top > bottom) notificationFilters[notificationFilter]();
  };

  const getTodayNotifications = () => {
    if (!loading) {
      loading = true;
      dispatch(getNotificationsBetween(-1, 0, ++page));
    }
  };
  const getThisWeekNotifications = () => {
    if (!loading) {
      loading = true;
      dispatch(getNotificationsBetween(-7, 0, ++page));
    }
  };
  const getLastWeekNotifications = () => {
    if (!loading) {
      loading = true;
      dispatch(getNotificationsBetween(-14, 7, ++page));
    }
  };
  const loadMoreNotifications = () => {
    if (!loading) {
      loading = true;
      dispatch(getNotificationsPage(++page));
    }
  };
  const notificationFilters = {
    today: getTodayNotifications,
    thisWeek: getThisWeekNotifications,
    lastWeek: getLastWeekNotifications,
    all: loadMoreNotifications,
  };
  const handleNotificationFilter = (val) => {
    setNotificationFilter(val);
    page = -1;
    loading = false; // race condition?
    notificationFilters[val]();
  };

  // mark all notifications as read / unread
  const markAllAsRead = async () => {
    //console.log("unreadNotifCount", unreadNotifCount);
    if (unreadNotifCount > 0) {
      await notificationsReadAll(true);
      handleNotificationFilter(notificationFilter);

      dispatch({
        type: "SET_UNREAD_NOTIFICATIONS_COUNT",
        payload: 0,
      });
    }
  };

  return (
    <Offcanvas className="notifcanvas" placement="end" show={show} onHide={handleClose}>
      <Offcanvas.Body>
        <Button className="position-absolute end-0 me-2" variant="close" onClick={handleClose} />
        <Tabs defaultActiveKey="notifications">
          {/*
          <Tab eventKey="tasks" title={t("OwnTasks", "Omat tehtävät")}>
            <div><ToggleButtonGroup type="radio" defaultValue={1} name="options1" size="sm">
              <ToggleButton id="tbg-radio-1" value={1}>{t("NewTasks", "Uudet")}</ToggleButton>
              <ToggleButton id="tbg-radio-2" value={2}>{t("Today", "Tänään")}</ToggleButton>
              <ToggleButton id="tbg-radio-3" value={3}>{t("ThisWeek", "Tällä viikolla")}</ToggleButton>
              <ToggleButton id="tbg-radio-4" value={4}>{t("All", "Kaikki")}</ToggleButton>
            </ToggleButtonGroup></div>
            {t("NoTasks", "Ei tehtäviä")}
          </Tab>
          */}

          <Tab
            eventKey="notifications"
            title={
              <>
                {t("Notifications", "Ilmoitukset")}
                {unreadNotifCount > 0 && <span className="notifcanvas-pill">{unreadNotifCount}</span>}
              </>
            }
          >
            <ToggleButtonGroup
              type="radio"
              defaultValue={notificationFilter}
              name="options2"
              size="sm"
              onChange={handleNotificationFilter}
            >
              <ToggleButton id="tbg-radio-5" value={"today"}>
                {t("Today", "Tänään")}
              </ToggleButton>
              <ToggleButton id="tbg-radio-6" value={"thisWeek"}>
                {t("ThisWeek", "Tällä viikolla")}
              </ToggleButton>
              <ToggleButton id="tbg-radio-7" value={"lastWeek"}>
                {t("LastWeek", "Viime viikolla")}
              </ToggleButton>
              <ToggleButton id="tbg-radio-8" value={"all"}>
                {t("All", "Kaikki")}
              </ToggleButton>
            </ToggleButtonGroup>
            <div className="notification-list" onScroll={notifScroll}>
              {notifs.length > 0
                ? notifs.map((notif, idx) => {
                  return (
                    <NotifCard
                      key={notif.notificationId}
                      notif={notif}
                      idx={idx}
                      relationType={notif.relationType}
                      duration={duration}
                      resolvedLanguage={resolvedLanguage}
                    />
                  );
                })
                : t("NoNotifications", "Ei ilmoituksia")}
            </div>
          </Tab>

          {/*
          <Tab eventKey="messages" title={t("Messages", "Viestit")}>
            <div><ToggleButtonGroup type="radio" defaultValue={9} name="options3" size="sm">
              <ToggleButton id="tbg-radio-9" value={9}>{t("NewTasks", "Uudet")}</ToggleButton>
              <ToggleButton id="tbg-radio-10" value={10}>{t("Today", "Tänään")}</ToggleButton>
              <ToggleButton id="tbg-radio-11" value={11}>{t("ThisWeek", "Tällä viikolla")}</ToggleButton>
              <ToggleButton id="tbg-radio-12" value={12}>{t("All", "Kaikki")}</ToggleButton>
            </ToggleButtonGroup></div>
            <div className="notification-list">{ msgs.length > 0
              ? msgs.map(notif => {
                const created = notif.updateInfoJSON?.createdDateTime ?? notif.timestamp;
                const createdDate = new Date(created);

                // hack to correct localdate with UTC server
                if (import.meta.env.PROD) {
                  createdDate.setUTCHours(createdDate.getUTCHours() + 2);
                }

                return (
                  <Card key={notif.notificationId} data-id={notif.notificationId}>
                    <Card.Header>
                      <time dateTime={created} title={createdDate.toLocaleString(resolvedLanguage)}>{duration(createdDate)}</time>
                    </Card.Header>
                    <Card.Body>
                      <FormCheck defaultChecked={notif.readByCurrentUser} onChange={read} title={t("IsRead", "Luettu")} />
                      <Card.Text>
                        {notif.sender}: {notif.content}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                );
              })
              : t("NoMessages", "Ei viestejä")
            }</div>
          </Tab>
          */}
        </Tabs>
      </Offcanvas.Body>
    </Offcanvas>
  );
}
